// External dependencies
import React, { useState } from "react";

// Material-UI components
import { TableRow, TableCell, IconButton, Collapse, Box } from "@mui/material";

// Internal dependencies
import AlertDialog from "components/AlertDialog";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import FormDialog from "components/Dialog/modal-popup";
import SnackBar from "components/SnackBar";
import { ConfirmationMessages, UserRoles } from "constants/constant";
import {
  _deleteUserRole,
  _updateUser,
} from "middlewares/ManageUsers/manageusers";
import AssignLocation from "./AssignLocation";
import { userRoleModel } from "./data/userRoleModel";
import { userRoles } from "./data/userRoles";

// MUI icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import { LocationMapping } from "pages/HomePage/data/HomePage.type";
import { useAppState } from "context/appState.context";
import { useParams } from "react-router-dom";

// Function to get role name by id
const getRoleNameById = (roleId?: string | number | undefined) => {
  const role = userRoles.find((r) => r.value === roleId!.toString());
  return role ? role.label : "";
};
type RowProps = {
  row: userRoleModel;
  getUserRoles: () => void;
  handleEditClick: () => void;
  locations: Array<LocationMapping>;
  getUserRoleSettings:()=>void;
};

/**
 * Row component
 *
 * This component renders a table row with user information and actions to edit, delete,
 * and assign locations to the user. It also supports expanding to show assigned locations.
 *
 * @param {RowProps} props - The props for the component.
 * @param {userRoleModel} props.row - The data of the user role.
 * @param {Function} props.getUserRoles - Function to fetch user roles.
 * @param {Function} props.handleEditClick - Function to handle edit action.
 * @param {Array<LocationMapping>} props.locations - List of locations to display.
 * @returns {JSX.Element} A component for rendering a row with user role information and actions.
 */
const Row: React.FC<Readonly<RowProps>> = (props) => {
  //State variables
  const [toggle, setToggle] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalForLocation, setOpenModalForLocation] = useState(false);
  const [userRoleId, setUserRoleId] = useState(0);
  const [userName, setUserName] = useState("");
  const [locationId, setLocationId] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationNames, setLocationNames] = useState<
    Array<{ locationId: number; locationName: string }>
  >([]);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });

  const { userRole } = useAppState();
  const { LocationId } = useParams();
  const isSuperAdmin = userRole === UserRoles.SuperAdmin || userRole === UserRoles.Admin;


  // Event handlers
  /**
   * Closes the modal dialog.
   */
  const handleClose = () => {
    setOpenModal(false);
  };

  /**
   * Closes the modal dialog for location assignment.
   */
  const handleCloseForLocation = () => {
    setOpenModalForLocation(false);
  };

    /**
   * Opens the modal dialog.
   */
  const openDialog = () => {
    setOpenModal(true);
  };
  /**
   * Opens the modal dialog for location assignment.
   */
  const openDialogForLocation = () => {
    setOpenModalForLocation(true);
  };

  /**
   * Closes the snackbar notification.
   * 
   * @param {AlertsProps} state - The state of the snackbar.
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

   /**
   * Hides the dialog.
   */
  const hideDialogHandler = () => setDialogOpen(false);

  /**
   * Deletes the user role.
   */
  const deleteUserRole = async () => {
    try {
      if (userRoleId !== undefined) {
        const res = await _deleteUserRole(userRoleId,Number(LocationId));
        if (res) {
          props.getUserRoles();
          props.getUserRoleSettings();
        }
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

   /**
   * Confirms the deletion of the user role.
   */
  const handleConfirm = () => {
    deleteUserRole();
    handleClose();
    handleCloseSnackBar({
      show: true,
      severity: "success",
      message: "Deleted Successfully !",
    });
  };

  /**
   * Confirms the location assignment update.
   */
  const handleLocationConfirm = () => {
    updateUser();
    handleClose();
    handleCloseSnackBar({
      show: true,
      severity: "success",
      message: "Deleted Successfully !",
    });
  };

  /**
   * Updates the user by removing the assigned location.
   */
  const updateUser = async () => {
    try {
      const { row } = props;
      const updatedUserRoleModel = { ...row };
      const locationIdsToRemove = [locationId];
      if (updatedUserRoleModel.locationMapping) {
        const locIds = updatedUserRoleModel.locationMapping.split(",");
        locationIdsToRemove.forEach((id) => {
          const index = locIds.indexOf(id.toString());
          if (index !== -1) {
            locIds.splice(index, 1);
          }
        });
        updatedUserRoleModel.locationMapping = locIds.join(",");
      }
      const res = await _updateUser(updatedUserRoleModel);
      if (res) {
        setToggle(!toggle);
        props.getUserRoles();
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

   /**
   * Handles the click event to delete a user role.
   * 
   * @param {number} userRoleId - The ID of the user role.
   * @param {string} userName - The name of the user.
   */
  const handleDeleteClick = (userRoleId: number, userName: string) => {
    openDialog();
    setUserRoleId(userRoleId);
    setUserName(userName);
  };

  /**
   * Handles the click event to delete an assigned location.
   * 
   * @param {number} userRoleId - The ID of the user role.
   * @param {string} userName - The name of the user.
   * @param {number} locationId - The ID of the location.
   */
  const handleLocationDeleteClick = (
    userRoleId: number,
    userName: string,
    locationId: number
  ) => {
    openDialogForLocation();
    setUserRoleId(userRoleId);
    setUserName(userName);
    setLocationId(locationId);
  };

  /**
   * Handles the click event to assign locations to a user role.
   * 
   * @param {number} userRoleId - The ID of the user role.
   */
  const handleAssignLocationClick = (userRoleId: number) => {
    setDialogOpen(true);
    setUserRoleId(userRoleId);
  };

  /**
   * Handles the click event to expand or collapse the row to show assigned locations.
   * 
   * @param {userRoleModel} userRoleModel - The user role model.
   */
  const handleExpand = async (userRoleModel: userRoleModel) => {
    try {
      const { locations } = props;
      const locationMapping = userRoleModel.locationMapping ?? "";
      const locationIds = locationMapping.split(",").map(Number);

      const filteredLocations = locations.filter((loc) =>
        locationIds.includes(loc.locationId)
      );
      const locData = filteredLocations.map((loc) => ({
        locationId: loc.locationId,
        locationName: loc.locationName,
      }));
      setLocationNames(locData);
    } catch (err) {
      console.error("Error:", err);
    }
    setToggle(!toggle);
  };

  return (
    <React.Fragment>
      <TableRow>
        <TableCell data-testid="userName" sx={{ width: "10%" }}>
          {props.row.userName}
        </TableCell>
        <TableCell data-testid="userEmail" sx={{ width: "8%" }}>
          {props.row.userEmail}
        </TableCell>
        <TableCell sx={{ width: "8%", border: "none" }} data-testid="company">
          {props.row.company}
        </TableCell>
        <TableCell data-testid="roleId" sx={{ width: "8%" }}>
          {getRoleNameById(props.row.roleId)}
        </TableCell>
        <TableCell sx={{ width: "12%" }}>
          <span title="Edit User" style={{ cursor: "pointer" }}>
            <EditIcon
              style={{
                cursor: "pointer",
                color: "#e4610f",
                marginRight: "8px",
              }}
              data-testid="edituser"
              onClick={props.handleEditClick}
            />
          </span>
          <span title="Delete User" style={{ cursor: "pointer" }}>
            <DeleteOutlineIcon
              data-testid="deleteIconTestId"
              onClick={() =>
                handleDeleteClick(
                  props.row.userRoleId ?? "",
                  props.row.userName ?? ""
                )
              }
              style={{
                color: "#e4610f",
                cursor: "pointer",
                marginRight: "8px",
              }}
            />
          </span>

          {isSuperAdmin && (
            <span title="Assign Location" style={{ cursor: "pointer" }}>
              <AddToPhotosIcon
                data-testid="assignLocation"
                style={{
                  color: "#e4610f",
                  cursor: "pointer",
                  marginRight: "8px",
                }}
                onClick={() =>
                  handleAssignLocationClick(props.row.userRoleId ?? "")
                }
              />
            </span>
          )}
          <span
            title="Expand Assigned Locations"
            style={{ cursor: "pointer" }}
          >
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => handleExpand(props.row)}
            >
              {toggle ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </span>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={toggle} timeout="auto" unmountOnExit>
            {toggle && (
              <Box
                sx={{
                  m: 2,
                  border: "1px solid #D1DCE9",
                  borderRadius: "4px",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          border: "1px solid #D1DCE9",
                          padding: "8px",
                        }}
                      >
                        Assigned Locations
                      </th>
                      <th
                        style={{
                          border: "1px solid #D1DCE9",
                          padding: "8px",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationNames.map((loc, index) => (
                      <tr key={loc.locationId}>
                        <td
                          style={{
                            border: "1px solid #D1DCE9",
                            padding: "8px",
                          }}
                        >
                          {loc.locationName}
                        </td>
                        <td
                          style={{
                            border: "1px solid #D1DCE9",
                            padding: "8px",
                          }}
                        >
                          <span
                            title="Delete Assigned Location"
                            style={{ cursor: "pointer" }}
                          >
                            <DeleteOutlineIcon
                              data-testid="deleteLocationIconTestId"
                              onClick={() =>
                                handleLocationDeleteClick(
                                  props.row.userRoleId ?? "",
                                  loc.locationName,
                                  loc.locationId
                                )
                              }
                              style={{
                                color: "#e4610f",
                                marginRight: "8px",
                              }}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          handleCloseSnackBar({ show: false, severity: "info", message: "" })
        }
      />
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={userName}
        confirmationMessage={ConfirmationMessages.UserConfirmation}
      />
      <AlertDialog
        open={openModalForLocation}
        openDialog={openDialogForLocation}
        handleClose={handleCloseForLocation}
        handleConfirm={handleLocationConfirm}
        locationName={userName}
        confirmationMessage={ConfirmationMessages.AssignLocationConfirmation}
      />
      <FormDialog
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandler}
        dialogTitle="Assign Location"
        isFullScreenAllowed={true}
        maxWidth="sm"
        childComponent={
          <AssignLocation
            userRoleModel={props.row}
            hideDialogHandler={hideDialogHandler}
            getUserRoles={props.getUserRoles}
            handleCloseSnackBar={handleCloseSnackBar}
            locations={props.locations}
          ></AssignLocation>
        }
      ></FormDialog>
    </React.Fragment>
  );
}

export default Row;
